import { useLinkTo } from '@react-navigation/native';
import { useGlobalConfig } from 'context/GlobalContext';
import { useSocket } from 'context/SocketContext';
import { RootStackScreenProps } from 'navigation/AppNavigator';
import React, { useCallback } from 'react';
import { Image, Text, View } from 'react-native';
import { BackGroundImageView, tvStyles } from 'screens/Login/styles';
import { useLogInMutation } from 'services/backend';
import { calcSizeForTv } from 'utils/index';
import { Platform } from 'utils/platform';

import BackgroundImage from 'assets/background.png';
import Logo from 'assets/loginLogo2x.png';

import NewGradientButton from 'components/Button/NewGradienteButton';
import { Icons } from 'components/Icons';
import { WebDiv } from 'components/WebDiv';
import IsLoading from 'components/isLoading';

const INFO_TEXTS = [
  <Text style={tvStyles.infoText}>Escaneie o QR Code ao lado</Text>,
  <Text style={tvStyles.infoText}>Faça login com seu usuário e senha</Text>,
  <Text style={[tvStyles.infoText, { flexShrink: 1 }]}>
    Confirme o acesso e aproveite o conteúdo do{' '}
    <Text style={[tvStyles.infoText, tvStyles.blueText]}>Bandplay</Text>!
  </Text>,
];

const CTVLoginScreen: React.FC<RootStackScreenProps<'Login'>> = ({ route }) => {
  const linkTo = useLinkTo();
  const { socket } = useSocket();
  const { LoginScreen, dataIsLoading } = useGlobalConfig();
  const [deviceAuth, setDeviceAuth] = React.useState<any>();
  const [isBtnFocused, setIsBtnFocused] = React.useState(false);
  const [logIn, { isLoading: loginIsLoading, isSuccess: loginIsSuccess }] = useLogInMutation();
  const timer = React.useRef<NodeJS.Timeout | null>(null);
  const RENEW_TIME = 5 * 60 * 1000;

  const btnOnFocus = () => (Platform.isAndroidTV ? setIsBtnFocused(true) : null);
  const btnOnBlur = () => (Platform.isAndroidTV ? setIsBtnFocused(false) : null);

  const handleLoginCode = useCallback(
    async (code: string) => {
      await logIn({ code }).unwrap().catch(console.error);
      linkTo(route.params?.redirect || '/');
    },
    [logIn, linkTo, route?.params?.redirect]
  );

  const fetchCode = () => {
    socket.emit('auth', { client_id: 'TrvVxuuhvllszYhOTGcgiYDAV' }, setDeviceAuth);
  };

  const handleRenewCode = () => {
    fetchCode();
    if (timer?.current) {
      clearTimeout(timer.current);
    }
    timer.current = setInterval(fetchCode, RENEW_TIME);
  };

  React.useEffect(() => {
    socket.connect();

    // recebe as credenciais
    socket.on('oauth/code', (code: string) => handleLoginCode(code));

    fetchCode();

    // renova code + qrcode a cada 5 minutos
    timer.current = setInterval(fetchCode, RENEW_TIME);

    return () => {
      if (timer.current) {
        clearInterval(timer.current);
      }
      socket.disconnect();
    };
  }, [socket, handleLoginCode]);

  if (!LoginScreen || dataIsLoading || loginIsLoading || loginIsSuccess) {
    return (
      <BackGroundImageView
        source={{
          uri:
            (typeof BackgroundImage !== 'number' && BackgroundImage) ||
            'https://img.band.com.br/image/2023/08/30/bgmobilebandplay-161926.png',
        }}>
        <View style={tvStyles.loadingContainer}>
          <IsLoading />
        </View>
      </BackGroundImageView>
    );
  }

  return (
    <View style={tvStyles.container}>
      <BackGroundImageView
        source={{
          uri:
            (typeof BackgroundImage !== 'number' && BackgroundImage) ||
            'https://img.band.com.br/image/2023/08/30/bgmobilebandplay-161926.png',
        }}>
        <View style={tvStyles.mainContent}>
          <Image source={Logo} style={tvStyles.logo} />

          <View style={tvStyles.contentRow}>
            <View style={tvStyles.leftSection}>
              <Text style={tvStyles.descriptionText}>{LoginScreen?.description}</Text>
              <View style={tvStyles.infoContainer}>
                {INFO_TEXTS.map((text, index) => (
                  <View style={tvStyles.infoRow} key={index}>
                    <View style={tvStyles.numberContainer}>
                      <Text style={tvStyles.numberText}>0{index + 1}</Text>
                    </View>
                    <View style={tvStyles.divider} />
                    {text}
                  </View>
                ))}
              </View>
            </View>

            <View style={tvStyles.rightSection}>
              {deviceAuth !== undefined ? (
                <>
                  <View style={tvStyles.qrCodeContainer}>
                    <View style={tvStyles.qrCodeWrapper}>
                      <Image source={{ uri: deviceAuth.qrcode }} style={tvStyles.qrCode} />
                    </View>
                    <Text style={tvStyles.codeText}>{deviceAuth.code}</Text>
                  </View>
                  <WebDiv className="navigation-container navigation-priority">
                    <NewGradientButton
                      hasTVPreferredFocus
                      onFocus={btnOnFocus}
                      onBlur={btnOnBlur}
                      gradientVariant="secondary"
                      role="button"
                      onPress={handleRenewCode}
                      style={{
                        paddingHorizontal: calcSizeForTv(52),
                        borderColor: isBtnFocused ? 'white' : 'transparent',
                        borderWidth: 2,
                        borderRadius: 25,
                        paddingVertical: calcSizeForTv(10),
                      }}>
                      <View style={tvStyles.buttonContent}>
                        <Icons.ArrowRotateLeft
                          width={calcSizeForTv(24).toString()}
                          height={calcSizeForTv(24).toString()}
                        />
                        <Text style={tvStyles.buttonText}>Atualizar QR Code</Text>
                      </View>
                    </NewGradientButton>
                  </WebDiv>
                </>
              ) : (
                <View>
                  <IsLoading />
                </View>
              )}
            </View>
          </View>
        </View>
      </BackGroundImageView>
    </View>
  );
};

export default CTVLoginScreen;
